import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScheduleSelect } from "@/components/molecules/schedule-select";
import type {
  CallDestination,
  CallDestinationKind,
  DestinationAvailability,
} from "@/features/connectors/types";
import { useAssignableDevices } from "@/features/devices/hooks/use-devices";
import { useTranslation } from "@/features/utils/i18n";
import { ArrowDown, ArrowUp, Plus, Trash } from "@phosphor-icons/react";
import { useParams } from "react-router-dom";

export interface TransferableMember {
  /** `client_members.id` — the id a destination stores. */
  id: string;
  name: string;
  /** Their mobile, when they have one — a member reached on a handset may not. */
  phone?: string;
  /** True when they answer on a VoIP handset rather than a mobile. */
  onDevice?: boolean;
}

/**
 * A login that can be rung on the phone of its own account.
 *
 * Separate from {@link TransferableMember} because they are separate rows: the
 * same person can be both, and picking one rather than the other is picking
 * which number rings. Someone who administers the tenant without being on the
 * team has no member row at all, and this is the only way to reach them.
 */
export interface RoutableUser {
  /** `users.id`. */
  id: string;
  name: string;
  phone?: string | null;
}

interface DestinationListProps {
  value: CallDestination[];
  onChange: (value: CallDestination[]) => void;
  members: TransferableMember[];
  /** Logins with a phone on their account. Not the same list as `members`. */
  users: RoutableUser[];
  /** Prefix for input ids, so two lists on one form stay accessible. */
  idPrefix: string;
  /** Shown above the list. */
  title: string;
  description?: string;
}

const emptyOfKind = (kind: CallDestinationKind): CallDestination => {
  switch (kind) {
    case "member":
      // A person's own hours are the obvious window and the reason the feature
      // exists, so a new member row starts gated. Every other kind has no
      // schedule of its own and starts open.
      return {
        kind: "member",
        member_id: "",
        availability: { kind: "member_schedule" },
      };
    case "user":
      // A login has no hours of its own — that is what makes it not a member.
      return { kind: "user", user_id: "" };
    case "device":
      return { kind: "device", device_id: "" };
    case "sip":
      return { kind: "sip", uri: "" };
    default:
      return { kind: "phone", number: "" };
  }
};

/** Is this row worth sending? An empty target is a row someone started and left. */
export const isDestinationFilled = (destination: CallDestination): boolean => {
  switch (destination.kind) {
    case "member":
      return !!destination.member_id;
    case "user":
      return !!destination.user_id;
    case "device":
      return !!destination.device_id;
    case "phone":
      return !!destination.number.trim();
    case "sip":
      return !!destination.uri.trim();
    default:
      return false;
  }
};

/** Drop the half-filled rows and trim what is left. */
export const cleanDestinations = (
  destinations: CallDestination[],
): CallDestination[] =>
  destinations.filter(isDestinationFilled).map((destination) => {
    if (destination.kind === "phone") {
      return { ...destination, number: destination.number.trim() };
    }
    if (destination.kind === "sip") {
      return { ...destination, uri: destination.uri.trim() };
    }
    return destination;
  });

/**
 * An ordered list of places a call can be sent.
 *
 * The order is the feature: the first destination we believe is reachable takes
 * the call, and one that rings out or fails to connect hands over to the next.
 * "Reachable" is decided server-side — a closed schedule, an unplugged handset
 * — which is why each row carries a window rather than the list carrying one.
 */
export const DestinationList = ({
  value,
  onChange,
  members,
  users,
  idPrefix,
  title,
  description,
}: DestinationListProps) => {
  const { t } = useTranslation("connectors");
  const { company: clientId } = useParams<{ company: string }>();
  const { data: devices = [] } = useAssignableDevices(clientId || "");

  const update = (index: number, destination: CallDestination) =>
    onChange(value.map((entry, i) => (i === index ? destination : entry)));

  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= value.length) return;
    const next = [...value];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  const availabilityOf = (
    destination: CallDestination,
  ): DestinationAvailability => destination.availability || { kind: "always" };

  return (
    <div className="space-y-2">
      <div className="space-y-1">
        <Label className="text-xs font-medium">{title}</Label>
        {description && (
          <p className="text-xs text-muted-foreground">{description}</p>
        )}
      </div>

      {value.length === 0 && (
        <p className="text-xs italic text-muted-foreground">
          {t("connectors.routing.destinations.empty")}
        </p>
      )}

      {value.map((destination, index) => {
        const availability = availabilityOf(destination);

        return (
          <div
            key={`${idPrefix}-${index}`}
            className="space-y-2 rounded-md border bg-background p-3"
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground">
                {t("connectors.routing.destinations.stepLabel", {
                  index: index + 1,
                })}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={index === 0}
                  aria-label={t("connectors.routing.destinations.moveUp")}
                  onClick={() => move(index, -1)}
                >
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={index === value.length - 1}
                  aria-label={t("connectors.routing.destinations.moveDown")}
                  onClick={() => move(index, 1)}
                >
                  <ArrowDown className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  aria-label={t("connectors.routing.destinations.remove")}
                  onClick={() => onChange(value.filter((_, i) => i !== index))}
                >
                  <Trash className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="grid gap-2 sm:grid-cols-2">
              <Select
                value={destination.kind}
                onValueChange={(kind) =>
                  update(index, emptyOfKind(kind as CallDestinationKind))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">
                    {t("connectors.routing.destinations.kindMember")}
                  </SelectItem>
                  <SelectItem value="user">
                    {t("connectors.routing.destinations.kindUser")}
                  </SelectItem>
                  <SelectItem value="phone">
                    {t("connectors.routing.destinations.kindPhone")}
                  </SelectItem>
                  <SelectItem value="device">
                    {t("connectors.routing.destinations.kindDevice")}
                  </SelectItem>
                  <SelectItem value="sip">
                    {t("connectors.routing.destinations.kindSip")}
                  </SelectItem>
                </SelectContent>
              </Select>

              {destination.kind === "member" && (
                <Select
                  value={destination.member_id}
                  onValueChange={(member_id) =>
                    update(index, { ...destination, member_id })
                  }
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t("connectors.routing.memberPlaceholder")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {members.map((member) => (
                      <SelectItem key={member.id} value={member.id}>
                        {member.name}
                        {member.onDevice
                          ? ` — ${t("connectors.routing.destinations.viaDevice")}`
                          : member.phone
                            ? ` (${member.phone})`
                            : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {destination.kind === "user" && (
                <Select
                  value={destination.user_id}
                  onValueChange={(user_id) =>
                    update(index, { ...destination, user_id })
                  }
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t("connectors.routing.userPlaceholder")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.name}
                        {user.phone ? ` (${user.phone})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {destination.kind === "phone" && (
                <Input
                  value={destination.number}
                  onChange={(e) =>
                    update(index, { ...destination, number: e.target.value })
                  }
                  placeholder={t("connectors.routing.numberPlaceholder")}
                />
              )}

              {destination.kind === "device" && (
                <Select
                  value={destination.device_id}
                  onValueChange={(device_id) =>
                    update(index, { ...destination, device_id })
                  }
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t(
                        "connectors.routing.destinations.devicePlaceholder",
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {devices.map((device) => (
                      <SelectItem key={device.id} value={device.id}>
                        {device.label}
                        {device.registered
                          ? ""
                          : ` — ${t("connectors.routing.destinations.deviceOffline")}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {destination.kind === "sip" && (
                <Input
                  value={destination.uri}
                  onChange={(e) =>
                    update(index, { ...destination, uri: e.target.value })
                  }
                  placeholder={t("connectors.routing.fallback.sipPlaceholder")}
                />
              )}
            </div>

            <div className="grid gap-2 sm:grid-cols-2">
              <Select
                value={availability.kind}
                onValueChange={(kind) =>
                  update(index, {
                    ...destination,
                    availability:
                      kind === "schedule"
                        ? { kind: "schedule", schedule_id: "" }
                        : kind === "member_schedule"
                          ? { kind: "member_schedule" }
                          : { kind: "always" },
                  } as CallDestination)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="always">
                    {t("connectors.routing.destinations.availabilityAlways")}
                  </SelectItem>
                  {destination.kind === "member" && (
                    <SelectItem value="member_schedule">
                      {t("connectors.routing.destinations.availabilityMember")}
                    </SelectItem>
                  )}
                  <SelectItem value="schedule">
                    {t("connectors.routing.destinations.availabilitySchedule")}
                  </SelectItem>
                </SelectContent>
              </Select>

              {availability.kind === "schedule" && (
                <ScheduleSelect
                  value={availability.schedule_id}
                  allowNone={false}
                  onChange={(schedule_id) =>
                    update(index, {
                      ...destination,
                      availability: { kind: "schedule", schedule_id },
                    } as CallDestination)
                  }
                />
              )}
            </div>
          </div>
        );
      })}

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => onChange([...value, emptyOfKind("member")])}
      >
        <Plus className="h-4 w-4" />
        {t("connectors.routing.destinations.add")}
      </Button>
    </div>
  );
};
